import React from 'react';

export interface AuthSplitLayoutProps {
  children: React.ReactNode;
  imageSrc?: string;
  testimonial?: {
    quote: string;
    author: string;
    role: string;
    location: string;
  };
  logo?: React.ReactNode;
  variant?: 'split' | 'single';
  className?: string;
  showPartnerBadge?: boolean;
  footerNode?: React.ReactNode;
}

export const AuthSplitLayout = ({
  children,
  imageSrc = 'https://good-neighbor-web-assets.s3.us-east-2.amazonaws.com/login-hero.jpg',
  testimonial = {
    quote: 'Volunteering in my community has changed my life forever.',
    author: 'John Smith',
    role: 'Volunteer',
    location: 'Los Angeles, CA'
  },
  logo,
  variant = 'split',
  className,
  showPartnerBadge = true,
  footerNode
}: AuthSplitLayoutProps) => {
  const isSingleColumn = variant === 'single';

  const baseClassName = isSingleColumn
    ? 'flex min-h-screen w-full bg-[#f8f3ed]'
    : 'flex min-h-screen w-full bg-brand-primary';

  return (
    <div className={className ?? baseClassName}>
      {/* Left Side - Form */}
      <div
        className={
          isSingleColumn
            ? 'relative flex min-h-screen w-full flex-col'
            : 'relative flex w-full flex-col lg:w-1/2'
        }
      >
        <div
          className={
            isSingleColumn
              ? 'flex flex-col flex-1 w-full px-6 py-8 lg:px-12 lg:py-12'
              : 'flex flex-col flex-1 w-full p-0 md:p-8 lg:p-12 xl:p-24 justify-start lg:justify-center'
          }
        >
          {isSingleColumn ? (
            <div className="flex items-center justify-between">
              <div className="h-8">
                {logo || (
                  <img src="/logo.png" alt="Good Neighbor" className="h-full" />
                )}
              </div>
              {showPartnerBadge && (
                <span className="text-sm font-semibold text-utility-brand-700 bg-utility-brand-50 border border-utility-brand-200 px-3 py-1 rounded-full">
                  Partner Portal
                </span>
              )}
            </div>
          ) : (
            <>
              <div className="absolute top-8 left-4 lg:top-12 lg:left-12">
                {logo || (
                  <img src="/logo.png" alt="Good Neighbor" className="h-8" />
                )}
              </div>
              {showPartnerBadge && (
                <div className="hidden lg:block absolute top-8 right-8 lg:top-12 lg:right-12">
                  <span className="text-sm font-semibold text-utility-brand-700 bg-utility-brand-50 border border-utility-brand-200 px-3 py-1 rounded-full">
                    Partner Portal
                  </span>
                </div>
              )}
            </>
          )}
          <div
            className={
              isSingleColumn
                ? 'flex w-full flex-1 items-start justify-center lg:items-center'
                : 'w-full max-w-md mx-auto mt-16 lg:mt-0'
            }
          >
            <div className={isSingleColumn ? 'w-full max-w-[40rem]' : 'w-full'}>
              {children}
            </div>
          </div>
        </div>
        {footerNode}
      </div>

      {/* Right Side - Image & Testimonial */}
      {!isSingleColumn && (
        <div className="hidden relative w-0 lg:block lg:w-1/2">
          <div className="relative h-full w-full overflow-hidden">
            <img
              src={imageSrc}
              alt="Volunteering"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute bottom-12 left-12 right-12 rounded-2xl bg-white/10 backdrop-blur-md p-8 text-white border border-white/20">
              <p className="mb-6 text-2xl font-medium leading-relaxed">
                “{testimonial.quote}”
              </p>
              <div className="flex flex-col gap-1">
                <span className="font-semibold text-lg">
                  {testimonial.author}
                </span>
                <div className="flex flex-col text-sm text-gray-200">
                  <span>{testimonial.location}</span>
                  <span>{testimonial.role}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
